import type {
  BaseServiceOptions,
  Pagination,
  TBaseCatalogs,
  TRecordAction,
  TRequestCatalog,
} from '@/core/types'
import {
  type TArticle,
  moduleServiceHandlers,
  type TArticles,
  serviceConfig,
  catalogServiceHandlers,
} from '../../..'
import { defaultPagination, useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { useCatalogStore } from '@/core/store/catalogs'
import { toValue } from 'vue'

const { model } = serviceConfig
export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, filters, handlers, enableQuery } = options

  const store = useCatalogStore()
  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TArticle>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const editRecord = useMutation<TArticle, AxiosError, TArticle>(
    (payload: TArticle) =>
      moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<TArticle, AxiosError, TArticle>(
    (payload: TArticle) => moduleServiceHandlers.addRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.addRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const actionRecord = useMutation<TArticle, AxiosError, TRecordAction>(
    (payload: TRecordAction) => moduleServiceHandlers.actionRecord(payload),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const requestCatalog = useMutation<
    TBaseCatalogs,
    AxiosError,
    TRequestCatalog
  >(
    (payload: Record<any, any>) =>
      catalogServiceHandlers.categoriesCatalog.requestCatalog(payload),
    {
      onSuccess: (payload) => {
        store.setCatalogs(payload)
      },
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadRecords = useQuery<TArticles, AxiosError>({
    queryKey: [model, currentPage, filters?.loadRecords],
    queryFn: () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecords,
    onError: (error) => errorHttpNotification(error),
    onSuccess: (data) => {
      if (data?.meta) {
        handlers?.onDataChange?.(data.meta ?? defaultPagination)
      }
    },
  })

  return {
    loadRecords,
    addRecord,
    editRecord,
    actionRecord,
    requestCatalog,
  }
}
